export class CryptoUtil {
  private static readonly encoder = new TextEncoder();
  private static readonly decoder = new TextDecoder();

  static async encrypt(value: string, key: string): Promise<string> {
    const cryptoKey = await this.getAesKey(key);
    const iv = crypto.getRandomValues(new Uint8Array(12));

    const encrypted = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv,
      },
      cryptoKey,
      this.encoder.encode(value),
    );

    const encryptedBytes = new Uint8Array(encrypted);
    const result = new Uint8Array(iv.length + encryptedBytes.length);

    result.set(iv);
    result.set(encryptedBytes, iv.length);

    return this.toBase64(result);
  }

  static async decrypt(value: string, key: string): Promise<string> {
    const data = this.fromBase64(value);

    const iv = data.slice(0, 12);
    const encrypted = data.slice(12);

    const cryptoKey = await this.getAesKey(key);

    const decrypted = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: this.toArrayBuffer(iv),
      },
      cryptoKey,
      this.toArrayBuffer(encrypted),
    );

    return this.decoder.decode(decrypted);
  }

  static async hash(value: string): Promise<string> {
    const hash = await crypto.subtle.digest('SHA-256', this.encoder.encode(value));

    return this.toHex(new Uint8Array(hash));
  }

  static async hashWithKey(value: string, key: string): Promise<string> {
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      this.encoder.encode(key),
      {
        name: 'HMAC',
        hash: 'SHA-256',
      },
      false,
      ['sign'],
    );

    const signature = await crypto.subtle.sign('HMAC', cryptoKey, this.encoder.encode(value));

    return this.toHex(new Uint8Array(signature));
  }

  static async hashWithSalt(value: string): Promise<string> {
    const salt = crypto.getRandomValues(new Uint8Array(16));

    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      this.encoder.encode(value),
      'PBKDF2',
      false,
      ['deriveBits'],
    );

    const derived = await crypto.subtle.deriveBits(
      {
        name: 'PBKDF2',
        salt,
        iterations: 100000,
        hash: 'SHA-256',
      },
      keyMaterial,
      256,
    );

    return `${this.toHex(salt)}:${this.toHex(new Uint8Array(derived))}`;
  }

  static async verifyHashWithSalt(value: string, hashed: string): Promise<boolean> {
    const [saltHex, hash] = hashed.split(':');

    if (!saltHex || !hash) {
      return false;
    }

    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      this.encoder.encode(value),
      'PBKDF2',
      false,
      ['deriveBits'],
    );

    const salt = crypto.getRandomValues(new Uint8Array(16));

    const derived = await crypto.subtle.deriveBits(
      {
        name: 'PBKDF2',
        salt: new Uint8Array(salt),
        iterations: 100000,
        hash: 'SHA-256',
      },
      keyMaterial,
      256,
    );

    return this.toHex(new Uint8Array(derived)) === hash;
  }

  private static async getAesKey(key: string): Promise<CryptoKey> {
    const hash = await crypto.subtle.digest('SHA-256', this.encoder.encode(key));

    return crypto.subtle.importKey(
      'raw',
      hash,
      {
        name: 'AES-GCM',
      },
      false,
      ['encrypt', 'decrypt'],
    );
  }

  private static toBase64(bytes: Uint8Array): string {
    let binary = '';

    for (const byte of bytes) {
      binary += String.fromCharCode(byte);
    }

    return btoa(binary);
  }

  private static fromBase64(value: string): Uint8Array {
    const binary = atob(value);
    const bytes = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }

    return bytes;
  }

  private static toHex(bytes: Uint8Array): string {
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  }

  private static fromHex(value: string): Uint8Array {
    const bytes = new Uint8Array(value.length / 2);

    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = parseInt(value.substring(i * 2, i * 2 + 2), 16);
    }

    return bytes;
  }

  private static toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
    return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
  }
}
